import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { fromJS, List, Map } from 'immutable';
import { Observable } from 'rxjs';
import {
  getAccountQuotas,
  getBanners,
  getCanvases,
  getCurrentPlan,
  getFeatures,
  getFeaturesData,
  getFeatureToggles,
  getPurchaseInfo,
  getSubscription,
  IState,
} from './features-data.reducer';
import { ProvisioningFeatures } from './provisioning-features.model';
import { AccountQuotas } from './sub-models/account-quotas.model';
import { Banner } from './sub-models/banner.model';
import { BannersCanvases } from './sub-models/banners-canvases.model';
import { Canvas } from './sub-models/canvas.model';
import { CurrentPlan } from './sub-models/current-plan.model';
import { Features } from './sub-models/features.model';
import { ProvisioningSubscription } from './sub-models/provisioning-subscription.model';
import { PurchaseInfo } from './sub-models/purchase-info.model';
import { Toggles } from './sub-models/toggles.model';
import { distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { FeaturesDataStateService } from './features-data-state.service';

@Injectable()
export class FeaturesDataStore {
  featuresData$: Observable<ProvisioningFeatures> = this._store
    .select(getFeaturesData)
    .pipe(
      filter((state: IState) => state.loaded),
      map((state: IState) => {
        if (state.fetchError) {
          return null;
        } else {
          return new ProvisioningFeatures({
            [ProvisioningFeatures.KEYS.FEATURES]: state.features,
            [ProvisioningFeatures.KEYS.SUBSCRIPTION]: state.subscription,
            [ProvisioningFeatures.KEYS.BANNERS_AND_CANVASES]:
              state.bannersCanvases,
          });
        }
      }),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );
  features$: Observable<Features> = this._store.select(getFeatures).pipe(
    filter((x) => !!x),
    distinctUntilChanged((a, b) => a && a.equals(b))
  );
  isAccountVerified$: Observable<boolean> = this._store
    .select(getFeatures)
    .pipe(
      filter((x) => !!x),
      map((feature) =>
        this._featureDataStateService.isAccountVerified(feature)
      ),
      distinctUntilChanged()
    );
  isAccountNotified$: Observable<boolean> = this._store
    .select(getFeatures)
    .pipe(
      filter((x) => !!x),
      map((feature) =>
        this._featureDataStateService.isAccountNotified(feature)
      ),
      distinctUntilChanged()
    );
  isAccountBlocked$: Observable<boolean> = this._store.select(getFeatures).pipe(
    filter((x) => !!x),
    map((feature) => this._featureDataStateService.isAccountBlocked(feature)),
    distinctUntilChanged()
  );
  featureToggles$: Observable<Toggles> = this._store
    .select(getFeatureToggles)
    .pipe(
      filter((x) => !!x),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );
  subscription$: Observable<ProvisioningSubscription> = this._store
    .select(getSubscription)
    .pipe(
      filter((x) => !!x),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );
  banners$: Observable<List<Banner>> = this._store.select(getBanners).pipe(
    filter((x) => !!x),
    distinctUntilChanged((a, b) => a && a.equals(b)),
    map(
      (banners: List<Banner>) =>
        banners.sortBy((banner) => banner.level) as List<Banner>
    )
  );
  canvases$: Observable<List<Canvas>> = this._store.select(getCanvases).pipe(
    filter((x) => !!x),
    distinctUntilChanged((a, b) => a && a.equals(b))
  );
  currentPlan$: Observable<CurrentPlan> = this._store
    .select(getCurrentPlan)
    .pipe(
      filter((x) => !!x),
      distinctUntilChanged((a, b) => a && a.equals(b))
    );

  constructor(
    private _store: Store<any>,
    private _featureDataStateService: FeaturesDataStateService
  ) {}

  getFeaturesData(): Observable<ProvisioningFeatures> {
    return this.featuresData$.pipe(take(1));
  }

  getFeatures(): Observable<Features> {
    return this.features$.pipe(take(1));
  }

  getAccountQuotas(): Observable<AccountQuotas> {
    return this._store.select(getAccountQuotas).pipe(
      filter((x) => !!x),
      take(1)
    );
  }

  canCreate(accountQuotaKey: string): Observable<boolean> {
    return this.getAccountQuotas().pipe(
      map(
        (accountQuotas: AccountQuotas) =>
          accountQuotas.get(accountQuotaKey).canCreate
      )
    );
  }

  getCurrentPlan(): Observable<CurrentPlan> {
    return this.currentPlan$.pipe(take(1));
  }

  getPurchaseInfo(): Observable<PurchaseInfo> {
    return this._store.select(getPurchaseInfo).pipe(
      filter((x) => !!x),
      take(1)
    );
  }

  getSubscription(): Observable<ProvisioningSubscription> {
    return this.subscription$.pipe(take(1));
  }

  getFeatureToggles(): Observable<Toggles> {
    return this.featureToggles$.pipe(take(1));
  }

  getBannersCanvases(): Observable<BannersCanvases> {
    return this.featuresData$.pipe(
      filter((x) => !!x),
      take(1),
      map((featuresData: ProvisioningFeatures) => featuresData.bannersCanvases),
      filter((x) => !!x)
    );
  }

  getFeaturePayloads(): Observable<Map<string, any>> {
    return this.features$.pipe(
      map((features: Features) =>
        fromJS((features && features.payloads) || {})
      ),
      take(1)
    );
  }

  isForbiddenAccess(featureName: string): Observable<boolean> {
    return this.getFeatureToggles().pipe(
      map((toggles: Toggles) => toggles.isForbidden(featureName))
    );
  }

  isNoAccess(featureName: string): Observable<boolean> {
    return this.getFeatureToggles().pipe(
      map((toggles: Toggles) => toggles.isNoAccess(featureName))
    );
  }

  isReadOnlyAccess(featureName: string): Observable<boolean> {
    return this.getFeatureToggles().pipe(
      map((toggles: Toggles) => toggles.isReadOnlyAccess(featureName))
    );
  }

  isAllAccess(featureName: string): Observable<boolean> {
    return this.getFeatureToggles().pipe(
      map((toggles: Toggles) => toggles.isAllAccess(featureName))
    );
  }

  hasFeatureHighlightCanvas(featureName: string): Observable<boolean> {
    return this.getBannersCanvases().pipe(
      map((bannersCanvases: BannersCanvases) =>
        bannersCanvases.hasFeatureHighlightCanvas(featureName)
      )
    );
  }
}
